import React from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Stack } from 'react-bootstrap';
import '../accreditation.scss';
import {
    AccreditationType,
    ApplicationStatus,
    LockOwner,
    ReviewerRole,
    ReviewStatus,
} from 'graphql/__generated__/graphql';
import AbandonApplicationModal from '../ApplicationForm/Components/AbandonApplicationModal';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';
import { getBadgeClass, getStatusDisplay } from 'utils/applicationStatusUtils';
import { LoadingSpinner } from '../../LoadingSpinner';
import _ from 'lodash';
import IApplicationReviewSummary from '../../../interfaces/IApplicationReviewSummary';

interface IApplicationBlockProps {
    application: IAccreditationApplication;
    accreditationType: AccreditationType;
    handleOnSelectApplication: () => void;
    handleOnViewApplication: () => void;
    handleOnAbandonApplication: () => void;
    handleOnSubmitForReview: () => void;
    handleOnSubmitApplication: () => void;
    handleOnSelectReview: (review: IApplicationReviewSummary) => void;
    updating: boolean;
}

const ReviewsList: React.FC<{
    reviews: IApplicationReviewSummary[];
    onSelectReview: (review: IApplicationReviewSummary) => void;
}> = ({ reviews, onSelectReview }) => {
    const latestReview = _.maxBy(reviews, (review) => review.updatedDate);
    const isEmdrReviewLatest = latestReview?.reviewerRole === ReviewerRole.Emdr;

    const filteredReviews = reviews?.filter(
        (review) =>
            review.reviewStatus !== ReviewStatus.Cancelled &&
            (review.reviewerRole === ReviewerRole.Emdr) === isEmdrReviewLatest
    );

    if (!filteredReviews?.length) return null;

    const getReviewStatusIcon = (status: ReviewStatus) => {
        switch (status) {
            case ReviewStatus.InProgress:
            case ReviewStatus.New:
                return <i className="bi bi-clock-history text-warning fs-5" />;
            case ReviewStatus.Complete:
                return <i className="bi bi-check2-circle text-success fs-5" />;
            case ReviewStatus.WaitingOnApplicant:
                return <i className="bi bi-x-circle text-danger fs-5" />;
            default:
                return null;
        }
    };

    return (
        <ul className="list-unstyled">
            {filteredReviews.map((review) => (
                <li key={review.applicationReviewId}>
                    {getReviewStatusIcon(review.reviewStatus!)}

                    {review.reviewerRole === ReviewerRole.Emdr &&
                        review.reviewStatus === ReviewStatus.WaitingOnApplicant && (
                            <span className="m-lg-2">
                                Application returned by the Accreditation Committee. Please click
                                ‘Amend application’ to see the feedback, and to amend and resubmit
                                your application.
                            </span>
                        )}

                    {review.reviewerRole === ReviewerRole.Emdr &&
                        review.reviewStatus !== ReviewStatus.WaitingOnApplicant && (
                            <span className="m-lg-2">
                                Currently being reviewed by the Accreditation Committee
                            </span>
                        )}

                    {review.reviewerRole !== ReviewerRole.Emdr && (
                        <>
                            {review.reviewStatus !== ReviewStatus.InProgress &&
                            review.reviewStatus !== ReviewStatus.New ? (
                                <Button onClick={() => onSelectReview(review)} variant="link">
                                    View {review.reviewer.name}'s evaluation
                                    <i className="bi bi-chevron-right ms-2" />
                                </Button>
                            ) : (
                                <span className={'ms-2'}>
                                    {review.reviewer.name}'s evaluation is in progress
                                </span>
                            )}
                        </>
                    )}
                </li>
            ))}
        </ul>
    );
};

const ActionButtons: React.FC<{
    application: IAccreditationApplication;
    accreditationType: AccreditationType;
    onSelectApplication: () => void;
    onViewApplication: () => void;
    onSubmitApplication: () => void;
    onSubmitForReview: () => void;
    onAbandonApplication: () => void;
    updating: boolean;
}> = ({
    application,
    accreditationType,
    onSelectApplication,
    onViewApplication,
    onSubmitApplication,
    onSubmitForReview,
    onAbandonApplication,
    updating,
}) => {
    const { applicationStatus, lockedBy, isPaid } = application;

    if (updating) return <LoadingSpinner />;

    if (lockedBy !== LockOwner.Applicant) {
        return (
            <Button variant="outline-secondary" onClick={onViewApplication}>
                View application
            </Button>
        );
    }

    if (applicationStatus === ApplicationStatus.ReadyToSubmit) {
        return (
            <Button className="btn btn-primary" disabled={updating} onClick={onSubmitApplication}>
                Submit to EMDR
            </Button>
        );
    }

    return (
        <>
            <Button variant="outline-secondary" onClick={onSelectApplication}>
                {applicationStatus === ApplicationStatus.NeedsWork
                    ? 'Amend application'
                    : 'Continue your application'}
            </Button>

            {applicationStatus === ApplicationStatus.ReadyForReview && isPaid && (
                <Button className="btn btn-primary" onClick={onSubmitForReview}>
                    Submit for review
                </Button>
            )}

            {applicationStatus === ApplicationStatus.InProgress && !isPaid && (
                <AbandonApplicationModal
                    onConfirm={onAbandonApplication}
                    accreditationType={accreditationType}
                />
            )}
        </>
    );
};

const ApplicationBlock: React.FC<IApplicationBlockProps> = ({
    application,
    updating,
    accreditationType,
    handleOnSelectApplication,
    handleOnSelectReview,
    handleOnViewApplication,
    handleOnAbandonApplication,
    handleOnSubmitApplication,
    handleOnSubmitForReview,
}) => {
    const { applicationStatus, lockedBy, reviews } = application;
    const { name, price, description } = accreditationType;

    const badgeClass = getBadgeClass(lockedBy, applicationStatus);

    return (
        <Card className="mb-3" border="light">
            <CardHeader className="bg-transparent border-0 p-0">
                {applicationStatus && (
                    <div className="end-pill-wrapper">
                        <Badge pill bg={badgeClass} className="floating-pill">
                            {applicationStatus === ApplicationStatus.InProgress &&
                                lockedBy === LockOwner.Applicant && (
                                    <i className="bi bi-pencil me-2" />
                                )}
                            {getStatusDisplay(applicationStatus, lockedBy)}
                        </Badge>
                    </div>
                )}
            </CardHeader>
            <CardBody>
                <h3 className="card-title mb-3 fw-bold">{name} accreditation</h3>

                {!applicationStatus && (
                    <p className="card-subtitle mb-2 fw-bold text-black">
                        Cost: &pound;{price.toFixed(2)}
                    </p>
                )}

                <p className="card-text">{description}</p>

                {!applicationStatus ? (
                    <Button variant="outline-secondary" onClick={handleOnSelectApplication}>
                        Apply for {name} accreditation
                    </Button>
                ) : (
                    <>
                        <ReviewsList
                            reviews={reviews ?? []}
                            onSelectReview={handleOnSelectReview}
                        />

                        <Stack direction="horizontal" className="mx-auto" gap={4}>
                            <ActionButtons
                                application={application}
                                accreditationType={accreditationType}
                                onSelectApplication={handleOnSelectApplication}
                                onViewApplication={handleOnViewApplication}
                                onSubmitApplication={handleOnSubmitApplication}
                                onSubmitForReview={handleOnSubmitForReview}
                                onAbandonApplication={handleOnAbandonApplication}
                                updating={updating}
                            />
                        </Stack>
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default ApplicationBlock;
